import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import FetchModule from '../../../Model/Share/Network/FetchModule';

import PhoneNumberRegisterModal, { formatPhoneNumber } from './PhoneNumberRegisterModal';
import MoreExplanationView from './MoreExplanationView';

import LanguageKoPath from '../../../image/LanguageKo.svg';
import { ReactComponent as EditSvg } from '../../../image/Edit.svg';
import { ReactComponent as ExitButtonSvg } from '../../../image/ExitButtonImage.svg';

// 서버 승인 상태 → 화면 상태 매핑 (APPLIED·REVIEWING: 심사 대기, APPROVED: 승인, REJECTED: 반려) //
function decideStatus(approvalStatus) {
  if (approvalStatus === 'APPROVED') return 'approved';
  if (approvalStatus === 'REJECTED') return 'rejected';

  return 'waiting';
}

// 서버 발신번호 DTO 목록 → 화면용 데이터 변환 //
function convertToPhoneNumberList(items) {
  return items.map((item) => ({
    id: item.id,
    senderName: item.alias,
    phone: item.phoneNumber,
    countryCode: item.countryCode || '+82',
    businessNumber: item.businessNumber || '',
    businessLicenseFileName: item.businessFileName || '',
    businessLicenseFileUrl: item.businessFileURL || '',
    telecomCertFileName: item.telecomCertificateFileName || '',
    telecomCertFileUrl: item.telecomCertificateFileURL || '',
    status: decideStatus(item.approvalStatus),
    rejectionReason: item.rejectionReason || '',
    isUse: item.isUse
  }));
}

export default function PhoneNumberManagementBox() {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isViewRegisterModal, setIsViewRegisterModal] = useState(false);
  const [registerModalMode, setRegisterModalMode] = useState('apply');
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);

  const [hoveredButtonIndex, setHoveredButtonIndex] = useState(null);
  const [countryFlagMap, setCountryFlagMap] = useState({});

  const [phoneNumberList, setPhoneNumberList] = useState([]);

  // 국가 목록 조회 - countryCode → flagUrl 맵 구성 (국기 동적 표시용) //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataOfCoachVersion2('sms_cert/country', 'GET');

        if (responseData.status === 200) {
          const newFlagMap = {};

          for (const country of responseData.data) {
            newFlagMap[country.countryCode] = country.flagUrl;
          }

          setCountryFlagMap(newFlagMap);
        }
      }
    )();
  }, [])

  // 발신번호 신청 목록 최초 조회 //
  useEffect(() => {
    getSenderNumberList();
  }, [])

  // 발신번호 신청 목록 조회 - 서버 응답을 화면용 데이터로 변환 //
  function getSenderNumberList() {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('message/sender-number', 'GET');

        if (responseData.status === 200) setPhoneNumberList(convertToPhoneNumberList(responseData.data));
      }
    )();
  }

  // 발신번호 사용 토글 클릭 - 사용 여부 변경 후 서버가 반환한 전체 목록으로 갱신 //
  function onClickUseToggle(data) {
    const requestData = {
      id: data.id,
      isUse: !data.isUse
    };

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('message/sender-number/use', 'POST', requestData);

        if (responseData.status === 200) setPhoneNumberList(convertToPhoneNumberList(responseData.data));
        else alert(responseData.error || t('messageSend.phoneNumberManagement.alertRequestFailed'));
      }
    )();
  }

  // 발신번호 삭제(X) 클릭 - 확인 후 삭제하고 목록 갱신 //
  function onClickDeletePhoneNumber(data) {
    if (!window.confirm(t('messageSend.phoneNumberManagement.confirmDelete'))) return;

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('message/sender-number/delete', 'POST', { id: data.id });

        if (responseData.status === 200) {
          if (Array.isArray(responseData.data)) setPhoneNumberList(convertToPhoneNumberList(responseData.data));
          else getSenderNumberList();
        }
        else alert(responseData.error || t('messageSend.phoneNumberManagement.alertRequestFailed'));
      }
    )();
  }

  // 번호 추가하기 클릭 - 신청 모드로 등록 모달 오픈 //
  function onClickAddPhoneNumber() {
    setRegisterModalMode('apply');
    setSelectedPhoneNumber(null);
    setIsViewRegisterModal(true);
  }

  // 편집 클릭 - 발신번호 심사 상태(대기/승인/반려) 모드로 등록 모달 오픈 //
  function onClickEditPhoneNumber(data) {
    setRegisterModalMode(data.status);
    setSelectedPhoneNumber(data);
    setIsViewRegisterModal(true);
  }

  // 등록 모달 닫기 //
  function onClickExitRegisterModal() {
    setIsViewRegisterModal(false);
  }

  // 등록 모달 심사 요청 제출 완료 - 모달 닫고 목록 갱신 //
  function onClickSubmitRegisterModal() {
    setIsViewRegisterModal(false);
    getSenderNumberList();
  }

  // 심사 대기중 상태 렌더 - 상태 텍스트 + ? 아이콘 호버 시 왼쪽에 검토 절차 안내 말풍선 표시 (CSS 호버 처리) //
  function renderWaitingStatus() {
    return (
      <WaitingStatusWrapper>
        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('messageSend.phoneNumberManagement.statusWaiting')}</Text>
        <MoreExplanationView
          text={t('messageSend.phoneNumberManagement.reviewTooltip')}
          maxWidth={349} />
      </WaitingStatusWrapper>
    );
  }

  return (
    <TopLevelWrapper>
      {
        isViewRegisterModal &&
        <PhoneNumberRegisterModal
          mode={registerModalMode}
          data={selectedPhoneNumber}
          onClickExit={onClickExitRegisterModal}
          onClickSubmit={onClickSubmitRegisterModal} />
      }
      <PhoneNumberManagementBoxWrapper>
        <PhoneNumberManagementInfoWrapper>
          {
            phoneNumberList.map((data, index) => (
              <PhoneNumberManagementInfo
                key={'PhoneNumber-' + index}
                borderColor={hoveredButtonIndex === index ? (data.status === 'rejected' ? theme.pointRed : theme.primary) : theme.subBackground}>
                <PhoneNumberManagementInfoLeftWrapper>
                  {
                    data.status === 'approved' ?
                      <SlideComponentWrapper onClick={() => onClickUseToggle(data)}>
                        <SlideOutBox isClicked={data.isUse}>
                          <SlideInBox isClicked={data.isUse} />
                        </SlideOutBox>
                      </SlideComponentWrapper>
                      :
                      <ToggleSpacer />
                  }
                  <PhoneNumberNameWrapper>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{data.senderName}</Text>
                  </PhoneNumberNameWrapper>
                  <PhoneNumberWrapper>
                    <CountryFlag src={countryFlagMap[data.countryCode] || LanguageKoPath} />
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{formatPhoneNumber(data.phone)}</Text>
                  </PhoneNumberWrapper>
                </PhoneNumberManagementInfoLeftWrapper>
                <StatusWrapper>
                  {
                    data.status === 'waiting' &&
                    renderWaitingStatus()
                  }
                  {
                    data.status === 'rejected' &&
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.pointRed}>{t('messageSend.phoneNumberManagement.statusRejected', { reason: data.rejectionReason || '' })}</Text>
                  }
                </StatusWrapper>
                <ModifyButton>
                  <PlusButtonWrapper>
                    <ExitButtonIcon
                      $hoverColor={data.status === 'rejected' ? theme.pointRed : theme.primary}
                      onMouseEnter={() => setHoveredButtonIndex(index)}
                      onMouseLeave={() => setHoveredButtonIndex(null)}
                      onClick={() => onClickDeletePhoneNumber(data)} />
                  </PlusButtonWrapper>
                  <EditIcon
                    $hoverColor={data.status === 'rejected' ? theme.pointRed : theme.primary}
                    onMouseEnter={() => setHoveredButtonIndex(index)}
                    onMouseLeave={() => setHoveredButtonIndex(null)}
                    onClick={() => onClickEditPhoneNumber(data)} />
                </ModifyButton>
              </PhoneNumberManagementInfo>
            ))
          }
        </PhoneNumberManagementInfoWrapper>
        
        <PhoneNumberAddButtonWrapper>
          <PhoneNumberAddButton onClick={onClickAddPhoneNumber}>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.primary}>+</Text>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.primary}>{t('messageSend.addPhoneNumber')}</Text>
          </PhoneNumberAddButton>
        </PhoneNumberAddButtonWrapper>
      </PhoneNumberManagementBoxWrapper>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 1035px;
`;

const PhoneNumberManagementBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin-top: 20px;

  width: 100%;

  gap: 30px;
`;

const PhoneNumberManagementInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  gap: 10px;
`;

const PhoneNumberManagementInfo = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 0 20px;

  box-sizing: border-box;

  width: 100%;
  height: 75px;

  border: 1px solid ${props => props.borderColor};
  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};

  transition: border 0.3s ease-in-out;
`;

const PhoneNumberManagementInfoLeftWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 602px;

  gap: 20px;
`;

const SlideComponentWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  box-sizing: border-box;
  width: fit-content;
  height: 100%;

  gap: 30px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SlideOutBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-shrink: 0;
  width: 69px;
  height: 30px;

  border: ${props => props.isClicked ? `1px solid ${props.theme.primary}` : `1px solid ${props.theme.line}`};
  border-radius: 8px;
  background-color: ${props => props.isClicked ? props.theme.primary : props.theme.subBackground};

  transition: all 0.3s ease-in-out;
`;

const SlideInBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-left: ${props => props.isClicked ? '41px' : '2px'};

  width: 26px;
  height: 26px;

  border-radius: 6px;
  background-color: ${props => props.isClicked ? props.theme.white : props.theme.gray};

  transition: all 0.3s ease-in-out;
`;

const ToggleSpacer = styled.div`
  width: 71px;
  height: 30px;

  flex-shrink: 0;
`;

const PhoneNumberNameWrapper = styled.div`
  justify-content: flex-start;
  align-items: center;

  width: 250px;
  height: 20px;
`;

const StatusWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;

  padding-left: 20px;
  box-sizing: border-box;

  gap: 5px;
`;

const WaitingStatusWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;
`;

const PhoneNumberWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 242px;
  height: 23px;

  gap: 10px;
`;

const CountryFlag = styled.img`
  width: auto;
  height: 23px;

  border-radius: 3px;

  object-fit: cover;
`;

const ModifyButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 59px;
  height: 100%;

  gap: 25px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const PlusButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ExitButtonIcon = styled(ExitButtonSvg)`
  width: 11px;
  height: 11px;

  flex-shrink: 0;

  & path, & rect {
    fill: ${props => props.theme.line};

    transition: fill 0.3s ease-in-out;
  }

  @media (hover: hover) {
    &:hover path, &:hover rect {
      fill: ${props => props.$hoverColor};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const EditIcon = styled(EditSvg)`
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.line};

    transition: fill 0.3s ease-in-out;
  }

  @media (hover: hover) {
    &:hover path {
      fill: ${props => props.$hoverColor};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const PhoneNumberAddButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  width: 100%;
`;

const PhoneNumberAddButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;
  width: 100%;
  height: 62px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect x='1' y='1' width='calc(100%25 - 2px)' height='calc(100%25 - 2px)' rx='10' ry='10' fill='none' stroke='%23FF8B48' stroke-width='1.5' stroke-dasharray='8 8'/%3E%3C/svg%3E");

  @media (hover: hover){
    &:hover {
      cursor: pointer;
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;
  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.hover};
    }
  }

  transition: all 0.3s ease-in-out;
`;
